import React, { useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PLAYLIST_ITEM_TYPES } from "../lib/constants";
import { Plus, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, Youtube, Film, Newspaper, Upload } from "lucide-react";
import { toast } from "sonner";

const ITEM_TYPE_META = {
  [PLAYLIST_ITEM_TYPES.IMAGE]: { label: "Imagem", icon: ImageIcon, hint: "URL ou upload de imagem (jpg, png, webp...)" },
  [PLAYLIST_ITEM_TYPES.YOUTUBE]: { label: "YouTube", icon: Youtube, hint: "Cole a URL do vídeo (watch ou youtu.be)" },
  [PLAYLIST_ITEM_TYPES.MP4]: { label: "MP4", icon: Film, hint: "URL externa ou upload (recomendado URL externa para vídeos > 5MB)" },
  [PLAYLIST_ITEM_TYPES.RSS]: { label: "RSS", icon: Newspaper, hint: "URL do feed RSS — exibido por segundos antes do próximo item" },
};

const newId = () => `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const PlaylistEditor = ({ playlist = [], shuffle = false, onChange }) => {
  const [draft, setDraft] = useState({ type: PLAYLIST_ITEM_TYPES.IMAGE, url: "", duration: 10 });
  const fileInputRef = useRef(null);

  const update = (list, shuf = shuffle) => onChange({ playlist: list, playlistShuffle: shuf });

  const addItem = () => {
    if (!draft.url || !draft.url.trim()) {
      toast.error("Informe uma URL para o item");
      return;
    }
    const item = {
      id: newId(),
      type: draft.type,
      url: draft.url.trim(),
      duration: Math.max(2, Number(draft.duration) || 10),
    };
    update([...playlist, item]);
    setDraft((p) => ({ ...p, url: "" }));
  };

  const removeItem = (id) => update(playlist.filter((i) => i.id !== id));

  const moveItem = (id, dir) => {
    const idx = playlist.findIndex((i) => i.id === id);
    if (idx < 0) return;
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= playlist.length) return;
    const arr = [...playlist];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    update(arr);
  };

  const updateItem = (id, patch) => {
    update(playlist.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newItems = [];
    for (const f of files) {
      const isImage = f.type.startsWith("image/");
      const isVideo = f.type.startsWith("video/");
      if (!isImage && !isVideo) {
        toast.warning(`${f.name}: formato não suportado`);
        continue;
      }
      if (isVideo && f.size > 8 * 1024 * 1024) {
        toast.warning(`${f.name}: vídeo grande (>${Math.round(f.size / 1024 / 1024)}MB) — recomendado usar URL externa`);
      }
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      newItems.push({
        id: newId(),
        type: isImage ? PLAYLIST_ITEM_TYPES.IMAGE : PLAYLIST_ITEM_TYPES.MP4,
        url: dataUrl,
        duration: isImage ? 10 : 30,
      });
    }
    if (newItems.length) {
      update([...playlist, ...newItems]);
      toast.success(`${newItems.length} item(ns) adicionado(s) à playlist`);
    }
    e.target.value = "";
  };

  return (
    <div className="space-y-6">
      {/* Add new item */}
      <div className="border border-border p-6 space-y-4">
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">Adicionar Item à Playlist</div>
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_140px_auto] gap-3 items-end">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">Tipo</Label>
            <Select value={draft.type} onValueChange={(v) => setDraft((p) => ({ ...p, type: v }))}>
              <SelectTrigger className="mt-2 h-11 rounded-none" data-testid="playlist-add-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ITEM_TYPE_META).map(([k, m]) => (
                  <SelectItem key={k} value={k} data-testid={`playlist-add-type-${k}`}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">URL</Label>
            <Input
              value={draft.url}
              onChange={(e) => setDraft((p) => ({ ...p, url: e.target.value }))}
              placeholder={ITEM_TYPE_META[draft.type]?.hint || "URL"}
              className="mt-2 h-11 rounded-none font-mono text-sm"
              data-testid="playlist-add-url"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">Duração (s)</Label>
            <Input
              type="number"
              min={2}
              value={draft.duration}
              onChange={(e) => setDraft((p) => ({ ...p, duration: e.target.value }))}
              className="mt-2 h-11 rounded-none"
              data-testid="playlist-add-duration"
            />
          </div>
          <Button onClick={addItem} className="rounded-none h-11" data-testid="playlist-add-btn">
            <Plus className="w-4 h-4 mr-2" /> Adicionar
          </Button>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3 pt-3 border-t border-border">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/mp4"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="playlist-upload-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="rounded-none"
            data-testid="playlist-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" /> Upload de imagens / vídeos
          </Button>
          <p className="text-xs text-foreground/60">
            Aceita múltiplos arquivos; tipo detectado automaticamente.
          </p>
        </div>
      </div>

      {/* Shuffle toggle */}
      <div className="flex items-center justify-between border border-border p-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-1">Modo Aleatório</div>
          <div className="text-sm text-foreground/70">Embaralhar a ordem dos itens a cada ciclo da playlist.</div>
        </div>
        <Switch
          checked={shuffle}
          onCheckedChange={(v) => update(playlist, v)}
          data-testid="playlist-shuffle-toggle"
        />
      </div>

      {/* Playlist items */}
      <div className="border border-border">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">
            {playlist.length} {playlist.length === 1 ? "item" : "itens"} na playlist
          </div>
          {playlist.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-none text-destructive hover:text-destructive"
              onClick={() => {
                if (window.confirm("Limpar toda a playlist?")) update([]);
              }}
              data-testid="playlist-clear-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Limpar tudo
            </Button>
          )}
        </div>

        {playlist.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-foreground/40">
            Nenhum item ainda. Use o formulário acima para adicionar imagens, vídeos do YouTube,
            MP4 ou feeds RSS.
          </div>
        ) : (
          playlist.map((item, idx) => {
            const meta = ITEM_TYPE_META[item.type] || ITEM_TYPE_META.image;
            const Icon = meta.icon;
            const isData = typeof item.url === "string" && item.url.startsWith("data:");
            return (
              <div
                key={item.id}
                className="flex items-center gap-4 px-4 md:px-6 py-4 border-b border-border last:border-b-0"
                data-testid={`playlist-item-${idx}`}
              >
                <div className="flex flex-col items-center gap-1 w-8">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-none h-6 w-6 p-0"
                    onClick={() => moveItem(item.id, -1)}
                    disabled={idx === 0}
                    data-testid={`playlist-up-${idx}`}
                  >
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <span className="text-xs font-mono text-foreground/40">{idx + 1}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-none h-6 w-6 p-0"
                    onClick={() => moveItem(item.id, 1)}
                    disabled={idx === playlist.length - 1}
                    data-testid={`playlist-down-${idx}`}
                  >
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                </div>

                {/* Preview */}
                <div className="w-20 h-14 flex-shrink-0 bg-muted flex items-center justify-center border border-border overflow-hidden">
                  {item.type === PLAYLIST_ITEM_TYPES.IMAGE ? (
                    <img src={item.url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Icon className="w-6 h-6 text-foreground/60" />
                  )}
                </div>

                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-[120px_1fr_100px] gap-2 items-center">
                  <Select value={item.type} onValueChange={(v) => updateItem(item.id, { type: v })}>
                    <SelectTrigger className="h-10 rounded-none text-xs" data-testid={`playlist-item-type-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ITEM_TYPE_META).map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={isData ? "[arquivo enviado]" : item.url}
                    onChange={(e) => updateItem(item.id, { url: e.target.value })}
                    className="h-10 rounded-none font-mono text-xs"
                    disabled={isData}
                    data-testid={`playlist-item-url-${idx}`}
                  />
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={2}
                      value={item.duration}
                      onChange={(e) =>
                        updateItem(item.id, { duration: Math.max(2, Number(e.target.value) || 10) })
                      }
                      className="h-10 rounded-none w-20"
                      data-testid={`playlist-item-duration-${idx}`}
                    />
                    <span className="text-xs text-foreground/60">s</span>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-none"
                  onClick={() => removeItem(item.id)}
                  data-testid={`playlist-item-remove-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
